import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface UsageHeatmapProps {
  companyId?: string | null;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function UsageHeatmap({ companyId }: UsageHeatmapProps) {
  const { data: heatmapData, isLoading } = useQuery({
    queryKey: ['usage-heatmap', companyId],
    queryFn: async () => {
      // First get user IDs in this company (if filtered)
      let userIds: string[] = [];
      if (companyId) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('company_id', companyId);
        userIds = profiles?.map(p => p.id) || [];
        if (userIds.length === 0) return null;
      }

      let query = supabase
        .from('simulation_evaluations')
        .select('created_at, user_id')
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .not('created_at', 'is', null);

      if (companyId && userIds.length > 0) {
        // Filter by users in this company
        query = query.in('user_id', userIds);
      }

      const { data } = await query.limit(1000);

      if (!data || data.length === 0) return null;

      // Initialize grid: 7 days x 24 hours
      const grid: number[][] = Array.from({ length: 7 }, () => 
        Array.from({ length: 24 }, () => 0)
      );

      // Count sessions per day/hour using Australia/Sydney timezone for consistency
      const timeZone = 'Australia/Sydney';
      data.forEach((evaluation) => {
        const date = new Date(evaluation.created_at);
        // Use Intl.DateTimeFormat to get correct day/hour in target timezone
        const dayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone });
        const hourFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone });
        
        const dayName = dayFormatter.format(date);
        const dayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(dayName);
        const hour = parseInt(hourFormatter.format(date), 10);
        
        if (dayIndex >= 0 && hour >= 0 && hour < 24) {
          grid[dayIndex][hour]++;
        }
      });

      // Find max for color scaling
      const maxCount = Math.max(...grid.flat(), 1);

      return { grid, maxCount };
    },
  });

  const getColor = (count: number, maxCount: number) => {
    if (count === 0) return 'bg-muted/30';
    const intensity = count / maxCount;
    if (intensity < 0.25) return 'bg-primary/20';
    if (intensity < 0.5) return 'bg-primary/40';
    if (intensity < 0.75) return 'bg-primary/60';
    return 'bg-primary';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Usage Heatmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!heatmapData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Usage Heatmap
          </CardTitle>
          <CardDescription>When users are most active</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            No activity data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Usage Heatmap
        </CardTitle>
        <CardDescription>When users are most active on the platform</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* Hour labels */}
          <div className="flex gap-0.5 ml-10">
            {HOURS.filter((h) => h % 3 === 0).map((hour) => (
              <div 
                key={hour} 
                className="text-xs text-muted-foreground"
                style={{ width: '36px', textAlign: 'center' }}
              >
                {hour === 0 ? '12a' : hour < 12 ? `${hour}a` : hour === 12 ? '12p' : `${hour - 12}p`}
              </div>
            ))}
          </div>

          {/* Grid */}
          {DAYS.map((day, dayIndex) => (
            <div key={day} className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground w-8">{day}</span>
              <div className="flex gap-0.5">
                {HOURS.map((hour) => (
                  <div
                    key={`${day}-${hour}`}
                    className={`w-3 h-3 rounded-sm ${getColor(
                      heatmapData.grid[dayIndex][hour],
                      heatmapData.maxCount
                    )} transition-colors hover:ring-1 hover:ring-primary`}
                    title={`${day} ${hour}:00 - ${heatmapData.grid[dayIndex][hour]} sessions`}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
            <span className="italic">Times shown in AEST</span>
            <div className="flex items-center gap-2">
              <span>Less</span>
              <div className="flex gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-muted/30" />
                <div className="w-3 h-3 rounded-sm bg-primary/20" />
                <div className="w-3 h-3 rounded-sm bg-primary/40" />
                <div className="w-3 h-3 rounded-sm bg-primary/60" />
                <div className="w-3 h-3 rounded-sm bg-primary" />
              </div>
              <span>More</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
